import React, { useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface FeatureRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FeatureRequestModal: React.FC<FeatureRequestModalProps> = ({ isOpen, onClose }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [useCase, setUseCase] = useState('');
  const [category, setCategory] = useState('feature');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Please enter a title for your feature request');
      return;
    }
    
    if (!description.trim()) {
      setError('Please provide a description of the feature');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const { data: session } = await supabase.auth.getSession();
      
      // Get user ID and email if available
      const userId = session.session?.user.id;
      const userEmail = session.session?.user.email;
      
      // Submit the feature request to Supabase
      const { error: submitError } = await supabase
        .from('feature_requests')
        .insert({
          title,
          description,
          use_case: useCase || null,
          user_id: userId || null,
          user_email: userEmail || null,
          category,
          status: 'submitted',
          created_at: new Date().toISOString()
        });
      
      if (submitError) {
        throw submitError;
      }
      
      // Show success state
      setSuccess(true);
      
      // Reset form
      setTitle('');
      setDescription('');
      setUseCase('');
      setCategory('feature');
      
      // Close modal after delay
      setTimeout(() => {
        onClose();
        // Reset success state after modal is closed
        setTimeout(() => {
          setSuccess(false);
        }, 300);
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting feature request:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit feature request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={() => !isSubmitting && onClose()}
    >
      <div 
        className="bg-white rounded-lg shadow-lg p-4 border border-gray-200 w-full max-w-md flex flex-col paper-texture-heavy animate-fade-in"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: '85vh' }}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-medium">Request a Feature</h3>
          {!isSubmitting && (
            <button
              type="button"
              onClick={onClose}
              className="text-haus-light-white hover:text-white p-2 rounded-full hover:bg-haus-dark-brown/20"
              aria-label="Close feature request form"
            >
              <X size={20} />
            </button>
          )}
        </div>
        
        {success ? (
          <div className="text-center py-8">
            <div className="mb-4 mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="32" 
                height="32" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="text-green-600"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h4 className="text-xl font-medium text-green-700 mb-2">Thank You!</h4>
            <p className="text-gray-600">
              Your feature request has been submitted successfully. We'll review it soon.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col overflow-auto">
            {error && (
              <div className="mb-3 p-2 text-sm rounded bg-red-50 text-red-700 border border-red-200">
                {error}
              </div>
            )}
            
            <div className="mb-3">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Feature Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-1 focus:ring-haus-dark-green"
                placeholder="What feature would you like to see?"
                disabled={isSubmitting}
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full border border-gray-300 rounded p-2 min-h-[100px] focus:outline-none focus:ring-1 focus:ring-haus-dark-green"
                placeholder="Please describe the feature in detail..."
                disabled={isSubmitting}
                required
              ></textarea>
            </div>
            
            <div className="mb-3">
              <label htmlFor="use-case" className="block text-sm font-medium text-gray-700 mb-1">
                Use Case
              </label>
              <textarea
                id="use-case"
                value={useCase}
                onChange={e => setUseCase(e.target.value)}
                className="w-full border border-gray-300 rounded p-2 min-h-[80px] focus:outline-none focus:ring-1 focus:ring-haus-dark-green"
                placeholder="How would you use this feature? (optional)"
                disabled={isSubmitting}
              ></textarea>
            </div>
            
            <div className="mb-4">
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                id="category"
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-1 focus:ring-haus-dark-green"
                disabled={isSubmitting}
              >
                <option value="feature">New Feature</option>
                <option value="enhancement">Enhancement</option>
                <option value="ux">User Experience</option>
                <option value="bug">Bug Fix</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-haus-dark-green text-white py-2 px-4 rounded ${
                isSubmitting 
                  ? 'opacity-70 cursor-not-allowed' 
                  : 'hover:bg-haus-light-olive'
              } transition-colors`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeatureRequestModal; 